/**
 * Language picker — native names, no flags (a flag is a country, not a
 * language). Used in the Shell header, the Login page and Settings.
 *
 * On change: bundle load → i18next switch → localStorage → and, when a user
 * is logged in, `PATCH /api/auth/me {lang}` so push notifications and AI
 * summaries follow the same choice on every device.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/lib/languages";
import { changeLanguage } from "@/lib/i18n";
import { isChunkLoadError } from "@/lib/appVersion";
import { markBuildStale } from "@/hooks/useNewVersion";
import { useLang } from "@/hooks/useDir";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import type { AuthUser } from "@/lib/auth";
import { IconAlertTriangle, IconGlobe } from "./icons";

interface Props {
  /** "compact" = icon + select for the header; "full" = labelled block for Settings. */
  variant?: "compact" | "full";
  className?: string;
}

export function LanguageSwitcher({ variant = "compact", className = "" }: Props) {
  const { t } = useTranslation();
  const lang = useLang();
  const user = useAuth((s) => s.user);
  const setSession = useAuth((s) => s.setSession);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onChange = async (code: string) => {
    if (code === lang) return;
    setBusy(true);
    setError(null);
    try {
      await changeLanguage(code);
      if (user) {
        const res = await api<{ token: string; user: AuthUser }>("/api/auth/me", {
          method: "PATCH",
          body: { lang: code },
        });
        setSession(res.token, res.user);
      }
    } catch (e) {
      // A locale chunk that no longer exists is not a translation failure —
      // this tab is older than the deployed build. Say THAT, via the banner,
      // instead of a message about saving that would send nobody anywhere.
      if (isChunkLoadError(e)) markBuildStale();
      else setError(t("common.languageSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const select = (
    <select
      aria-label={t("common.language")}
      value={lang}
      disabled={busy}
      onChange={(e) => void onChange(e.target.value)}
      className={
        variant === "compact"
          ? "text-xs bg-transparent border border-slate-200 rounded-lg px-2 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          : "px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white disabled:opacity-50"
      }
    >
      {LANGUAGES.map((l) => (
        <option key={l.code} value={l.code} dir={l.dir}>
          {l.nativeName}
        </option>
      ))}
    </select>
  );

  if (variant === "compact") {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`} title={t("common.language")}>
        <IconGlobe className="w-4 h-4 text-slate-400" />
        {select}
        {/* The header variant used to set this state and render nothing, so a
            failed save was invisible exactly where the switcher is used most. */}
        {error && (
          <span role="alert" title={error} className="text-red-600 inline-flex">
            <IconAlertTriangle className="w-4 h-4" />
          </span>
        )}
      </span>
    );
  }

  return (
    <div className={className}>
      <label className="block text-xs text-slate-500 mb-1">{t("common.language")}</label>
      {select}
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

export default LanguageSwitcher;
